import { useCallback } from 'react';
import useAxios from '@Apis/useAxios';
import {
    GET_CLUB_INVENTORY_OPTIONS_API,
    POST_CLUB_INVENTORY_OVERVIEW_API,
    POST_CLUB_INVENTORY_RECORDS_API,
    POST_CLUB_INVENTORY_DEFECTIVE_RECORDS_API,
    POST_CLUB_INVENTORY_PRODUCT_STOCK_OVERVIEW_API,
    POST_CLUB_INVENTORY_PRODUCT_STOCK_DETAIL_API,
} from '@Apis/apiPath';

const useClubInventoryApi = () => {
    const Axios = useAxios();

    /**
     * 取得店舖庫存頁面選項
     * @returns {Promise<{ recordRangeOptions: Array<{text:string,value:string}>, clubOptions: Array<{text:string,value:string}>, categoryOptions: Array<{text:string,value:string}> }>}
     * 對應後端 ClubInventoryOptionResponseModel（recordRangeOptions 異動區間 / clubOptions 店舖 / categoryOptions 分類）
     */
    const getClubInventoryOptionsApi = useCallback(
        async () => await Axios.get(GET_CLUB_INVENTORY_OPTIONS_API),
        // eslint-disable-next-line
        [],
    );

    /**
     * 跨店庫存總覽（對應後端 ClubInventoryOverviewRequestModel）
     * @param {object} params
     * @param {number} params.clubID 店舖ID（0 = 全部店舖）
     * @param {string} [params.keyword] 關鍵字（商品名稱 / 規格 / SKU）
     * @param {boolean} [params.isDefective] 是否僅查瑕疵庫，預設 false
     * @param {number|string} [params.saleCategory] 分類代碼（搜尋列欄位，送出時轉為後端 saleCategoryID；0 = 全部）
     * @param {number} [params.pageIndex] 頁碼，預設 1
     * @param {number} [params.pageSize] 每頁筆數，預設 20
     * @returns {Promise<{ list:any[], pageIndex:number, pageSize:number, totalCount:number, totalPage:number }>} PaginationResponseModel
     */
    const postClubInventoryOverviewApi = useCallback(
        async (params) =>
            await Axios.post(POST_CLUB_INVENTORY_OVERVIEW_API, {
                clubID: Number(params?.clubID) || 0,
                keyword: params?.keyword ?? '',
                isDefective: Boolean(params?.isDefective),
                // 前端搜尋列送 saleCategory，後端收 SaleCategoryID
                saleCategoryID: Number(params?.saleCategory ?? params?.saleCategoryID) || 0,
                pageIndex: Number(params?.pageIndex) || 1,
                pageSize: Number(params?.pageSize) || 20,
            }),
        // eslint-disable-next-line
        [],
    );

    /**
     * 店舖庫存異動紀錄分頁（對應後端 ClubInventoryRecordPageRequestModel；params 直送後端，欄位名需與後端一致）
     * @param {object} params
     * @param {number} params.clubID 店舖ID
     * @param {number} params.productID 商品ID
     * @param {number} params.variantID 變體ID
     * @param {number} [params.recordRange] 記錄區間：0 全部 / 1 盤點後 / 2 盤點前
     * @param {number} [params.recordType] 異動類型，預設 0（全部）
     * @param {number} [params.referenceType] 來源類型，預設 0（全部）
     * @param {string} [params.startDate] 開始日期（ISO 字串）
     * @param {string} [params.endDate] 結束日期（ISO 字串）
     * @param {number} [params.pageIndex] 頁碼，預設 1
     * @param {number} [params.pageSize] 每頁筆數，預設 10
     * @returns {Promise<{ detail:object, summary:object, records:{ list:any[], pageIndex:number, pageSize:number, totalCount:number, totalPage:number } }>} ClubInventoryRecordPageResponseModel
     */
    const postClubInventoryRecordsApi = useCallback(
        async (params) => {
            const resp = await Axios.post(POST_CLUB_INVENTORY_RECORDS_API, params);
            return resp;
        },
        // eslint-disable-next-line
        [],
    );

    /**
     * 商品角度總覽（對應後端 ClubProductInventoryOverviewRequestModel）
     * - SKU 為模糊搜尋（大小寫不敏感的部分匹配，由後端處理）
     * - 每一列實際上是「商品 + 變體」的組合（同一 productID 會因多個變體出現多次），
     *   因此補上前端合成的唯一 id（`${productID}-${variantID}`），
     *   避免 <Overview idKey="productID"> 用重複的 productID 當 React key 造成表格畫面錯亂。
     * @param {object} params
     * @param {string} [params.sku] SKU 關鍵字
     * @param {number} [params.pageIndex] 頁碼，預設 1
     * @param {number} [params.pageSize] 每頁筆數，預設 20
     * @returns {Promise<{ list:any[], pageIndex:number, pageSize:number, totalCount:number, totalPage:number }>}
     */
    const postClubInventoryProductStockOverviewApi = useCallback(
        async (params) => {
            const resp = await Axios.post(POST_CLUB_INVENTORY_PRODUCT_STOCK_OVERVIEW_API, {
                sku: params?.sku ?? '',
                pageIndex: Number(params?.pageIndex) || 1,
                pageSize: Number(params?.pageSize) || 20,
            });

            return {
                ...resp,
                list: (resp?.list || []).map((item) => ({
                    ...item,
                    id: `${item?.productID}-${item?.variantID}`,
                })),
            };
        },
        // eslint-disable-next-line
        [],
    );

    /**
     * 商品角度明細：回傳該商品在各店舖的庫存（不分頁，對應後端 ClubProductInventoryDetailRequestModel）
     * @param {object} params
     * @param {number} params.productID 商品ID
     * @param {number} params.variantID 變體ID
     * @returns {Promise<{ detail:object|null, clubStocks:Array<{clubID:number,clubName:string,sellableQty:number,defectQty:number}>, totalCount:number }>}
     */
    const postClubInventoryProductStockDetailApi = useCallback(
        async (params) => {
            const resp = await Axios.post(POST_CLUB_INVENTORY_PRODUCT_STOCK_DETAIL_API, {
                productID: Number(params?.productID) || 0,
                variantID: Number(params?.variantID) || 0,
            });
            return {
                detail: resp?.detail ?? null,
                clubStocks: Array.isArray(resp?.clubStocks) ? resp.clubStocks : [],
                totalCount: resp?.totalCount ?? 0,
            };
        },
        // eslint-disable-next-line
        [],
    );

    /**
     * 店舖庫存瑕疵紀錄分頁（對應後端 ClubInventoryDefectiveRecordPageRequestModel）
     * @param {object} params
     * @param {number} params.clubID 店舖ID
     * @param {number} params.productID 商品ID
     * @param {number} params.variantID 變體ID
     * @param {number} [params.pageIndex] 頁碼，預設 1
     * @param {number} [params.pageSize] 每頁筆數，預設 10
     * @returns {Promise<{ detail:object, records:{ list:any[], pageIndex:number, pageSize:number, totalCount:number, totalPage:number } }>} ClubInventoryDefectiveRecordPageResponseModel
     */
    const postClubInventoryDefectiveRecordsApi = useCallback(
        async (params) =>
            await Axios.post(POST_CLUB_INVENTORY_DEFECTIVE_RECORDS_API, {
                clubID: Number(params?.clubID) || 0,
                productID: Number(params?.productID) || 0,
                variantID: Number(params?.variantID) || 0,
                pageIndex: Number(params?.pageIndex) || 1,
                pageSize: Number(params?.pageSize) || 10,
            }),
        // eslint-disable-next-line
        [],
    );

    return {
        getClubInventoryOptionsApi,
        postClubInventoryOverviewApi,
        postClubInventoryRecordsApi,
        postClubInventoryDefectiveRecordsApi,
        postClubInventoryProductStockOverviewApi,
        postClubInventoryProductStockDetailApi,
    };
};

export default useClubInventoryApi;
